import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronLeftIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/solid';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { readIdentityFile, writeIdentityFile, deleteIdentityFile } from '../moduleServices/identity_services';
import { useClickOutside } from 'services/useClickOutside';
import useOverlayHistory from 'services/useOverlayHistory';
import useScrollLock from 'services/useScrollLock';
import SpinnerLoad from 'shared_items/SpinnerLoad';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const DrawerFile = ({ isOpen, onClose, file, onUpdate, onDelete }) => {
  const [content, setContent] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const menuRef = useRef(null);
  const timeoutRef = useRef(null);
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  const interceptPop = useCallback(() => {
    if (!isEditModeRef.current) return false;
    setIsEditMode(false);
    return true;
  }, []);

  const handleRequestClose = useOverlayHistory(isOpen, onClose, {
    stateKey: 'identityDrawer',
    interceptPop,
  });

  useClickOutside([menuRef], () => setShowMenu(false), showMenu);
  useScrollLock(isOpen);

  // Animation
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsEditMode(false);
        setContent('');
        setEditContent('');
      }, 300);
    }
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [isOpen]);

  // Load file content when drawer opens
  useEffect(() => {
    if (!isOpen || !file) return;
    setIsEditMode(false);
    setIsLoading(true);
    readIdentityFile(file.path)
      .then(data => {
        setContent(data.content || '');
        setEditContent(data.content || '');
      })
      .catch(() => toast.error('Failed to load file content'))
      .finally(() => setIsLoading(false));
  }, [isOpen, file?.path]);

  // ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || !isOpen || isSubmitting) return;
      e.preventDefault();
      if (isEditMode) setIsEditMode(false);
      else handleRequestClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isEditMode, isSubmitting, handleRequestClose]);

  const handleSave = async () => {
    if (editContent === content) { setIsEditMode(false); return; }
    setIsSubmitting(true);
    try {
      const result = await writeIdentityFile(file.path, editContent);
      setContent(editContent);
      setIsEditMode(false);
      onUpdate({ ...file, ...result });
      toast.success('File saved');
    } catch {
      toast.error('Failed to save file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await deleteIdentityFile(file.path);
      onDelete(file.path);
      toast.success('File deleted');
      handleRequestClose();
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setIsSubmitting(false);
      setShowDeleteModal(false);
    }
  };

  const fileName = file?.path?.split('/').pop() || '';

  if (!shouldRender) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${
          isVisible ? 'opacity-30' : 'opacity-0'
        }`}
        onClick={handleRequestClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[60%] max-w-[800px] bg-white shadow-xl z-[60] transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">

          {/* Delete confirmation modal */}
          {showDeleteModal && (
            <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center p-6">
              <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete file</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete{' '}
                  <span className="font-medium text-gray-700">&ldquo;{fileName}&rdquo;</span>?{' '}
                  This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-white flex-shrink-0">
            <button
              onClick={handleRequestClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>

            <div className="flex-1 text-center px-2 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{fileName}</p>
              <p className="text-xs text-gray-400 truncate">{file?.path}</p>
            </div>

            <div className="flex items-center gap-1">
              {isEditMode ? (
                <>
                  <button
                    onClick={() => setIsEditMode(false)}
                    disabled={isSubmitting}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSubmitting || editContent === content}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 disabled:opacity-40 transition-colors"
                  >
                    <CheckIcon className="h-5 w-5" strokeWidth={2.5} />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setEditContent(content); setIsEditMode(true); }}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}

              {/* Three-dot menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(v => !v)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>
                {showMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[140px] z-50">
                    <button
                      onClick={() => { setShowMenu(false); setShowDeleteModal(true); }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* File meta */}
          {file?.updated_at && !isEditMode && (
            <div className="px-6 py-2 bg-gray-50 border-b flex-shrink-0">
              <p className="text-xs text-gray-400">Last updated: {formatDate(file.updated_at)}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {isLoading ? (
              <div className="flex flex-1 items-center justify-center" aria-busy="true" aria-label="Loading">
                <SpinnerLoad moduleKey="identity" />
              </div>
            ) : isEditMode ? (
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                className="flex-1 w-full p-6 font-mono text-sm text-gray-700 resize-none focus:outline-none"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(transparent, transparent 1.4em, #e5e7eb 1.4em, #e5e7eb 1.5em)',
                  backgroundAttachment: 'local',
                  lineHeight: '1.5em',
                  backgroundSize: '100% 1.5em',
                }}
                placeholder="File content..."
              />
            ) : (
              <div className="flex-1 overflow-y-auto p-6">
                {content ? (
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 leading-relaxed break-words">
                    {content}
                  </pre>
                ) : (
                  <p className="text-sm text-gray-400 italic">Empty file</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DrawerFile;
